import { Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { isEqual, omit } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { displayErrorNotification } from '@utils/utils';

import CertificatePowerForm from './components/CertificatePowerForm';

import { AppState } from '@app/store/types';
import { getMandates, setMandates } from '@app/store/modules/user';

import { powersValidationSchema } from './validationSchema';

import {
  ISelectedCertificatePowerProps as IProps,
  IMandatesObj,
  IMandateItem,
} from './types';
import { Nullable } from '@app/types';
import { IMandate, IMandateCreate } from '@app/store/modules/user/types';
import MandatesService from '@services/mandates';
import { resetModal } from '@app/store/modules/modal';
import { useAppSelector } from '@app/hooks/redux';

const CertificatePower: FC<IProps> = (props) => {
  const {
    isEdit,
    onClose,
    boxOguid,
    onPowerOfAttorneySelect,
    inProgress,
    setInProgress,
  } = props;

  const formRef = useRef<FormikProps<IMandatesObj>>(null);

  const { t } = useTranslation();
  const { profile, mandates } = useAppSelector((state) => state.user);
  const { boxes } = useAppSelector((state) => state.boxes);

  const currentBox = boxes.find((val) => val.oguid === boxOguid);
  const boxTitleName = currentBox?.companyName ?? profile.fullName;
  const boxTitleEmail = currentBox?.email ?? profile.email;
  const boxTitle =
    boxOguid && currentBox ? `${boxTitleName} [${boxTitleEmail}]` : '';

  const [currentMandates, setCurrentMandates] = useState<IMandate[]>([]);
  const [powers, setPowers] = useState<IMandatesObj>({});
  const [initErrors, setInitErrors] = useState<
    FormikErrors<IMandatesObj> & IMandatesObj
  >({});
  const [isSigning, setIsSigning] = useState<boolean>(false);
  const [isSigningWithout, setIsSigningWithout] = useState<boolean>(false);
  const dispatch = useDispatch<any>();

  const {
    profile: { oguid: userOguid },
  } = useSelector((state: AppState) => state.user);

  const powerOfAttorneyTemplate: IMandateItem = {
    /* Пользовательский идентификатор объекта */
    guid: '',
    /* Идентификатор бизнес-ящика */
    boxOguid: boxOguid ?? '',
    /* Идентификатор пользователя */
    userOguid: userOguid,
    validFrom: '',
    validTo: '',
    issuedDate: '',
    organizationName: '',
    organizationInn: '',
    description: '',
    idx: 0,
  };

  const setInitPowers = async (
    errors?: FormikErrors<IMandatesObj> & IMandatesObj
  ) => {
    try {
      const newMandates = boxOguid
        ? await MandatesService.getListBox(boxOguid)
        : await MandatesService.getList();

      const newMandatesData = newMandates.data;

      if (!boxOguid) {
        dispatch(setMandates(newMandatesData));
      } else {
        const newMandatesState = [
          ...newMandatesData,
          ...mandates.filter(
            (val) =>
              !newMandatesData.find((newVal) => newVal.oguid === val.oguid)
          ),
        ];

        dispatch(setMandates(newMandatesState));
      }

      setCurrentMandates(newMandatesData);
      const newPowers = newMandatesData.reduce<IMandatesObj>(
        (acc, val, idx) => {
          const newAcc = {
            ...acc,
            [idx]: {
              ...val,
              idx,
            },
          };

          return newAcc;
        },
        {} as IMandatesObj
      );
      setPowers(newPowers);
      if (errors) {
        setInitErrors(errors);
      }
    } catch (err: any) {
      displayErrorNotification(err);
    }
  };

  useEffect(() => {
    if (!inProgress) {
      setInitPowers();
    }
  }, [inProgress]);

  useEffect(() => {
    const modalElement = window.document.querySelector(
      '.fl-modal_content-wrapper'
    );
    if (isEdit) {
      if (boxOguid) {
        modalElement?.classList.add('modal__width-special-no-box');
      } else {
        modalElement?.classList.add('modal__width-special');
      }
    } else {
      modalElement?.classList.add('modal__width-special-no-edit');
    }

    return () => {
      modalElement?.classList.remove('modal__width-special');
      modalElement?.classList.remove('modal__width-special-no-edit');
      modalElement?.classList.remove('modal__width-special-no-box');
    };
  }, [isEdit, boxOguid]);

  const handlePoAAdd = (): void => {
    if (!formRef.current) return;

    const { values } = formRef.current;

    const newIdx = Object.keys(values).length;
    const newPowerOfAttorneys = {
      ...values,
      [Object.keys(values).length]: {
        ...powerOfAttorneyTemplate,
        idx: newIdx,
      },
    };

    setPowers(newPowerOfAttorneys);
  };

  const handlePoARemove = (idx: string) => () => {
    if (!formRef.current) return;

    const { values } = formRef.current;

    const newValues = omit(values, idx);
    setPowers(newValues);
  };

  const handlePoASelect = (oguid: Nullable<string>) => () => {
    setIsSigning(true);
    if (!oguid) setIsSigningWithout(true);

    onPowerOfAttorneySelect?.(oguid);
  };

  const handleClose = (): void => {
    onClose?.();

    if (isEdit && !boxOguid) {
      dispatch(resetModal());
    }
  };

  const handleFormSubmit = async (
    values: IMandatesObj,
    formikHelpers: FormikHelpers<IMandatesObj>
  ): Promise<void> => {
    if (!isEdit) {
      return handlePoASelect(null)();
    }

    setInProgress?.(true);
    let hasError = false;

    for (const value of Object.values(values)) {
      const newValue: IMandateCreate = omit(value, 'oguid', 'idx');
      if (value.oguid) {
        const mandate = currentMandates.find(
          (val) => val.oguid === value.oguid
        );
        if (mandate) {
          const mandateItem = omit(mandate, 'oguid');
          if (!isEqual(newValue, mandateItem)) {
            await MandatesService.update(value.oguid, newValue).catch((err) => {
              hasError = true;
              formikHelpers.setFieldError(
                value.idx + '',
                err.response?.data?.message ?? 'error'
              );
              displayErrorNotification(err);
            });
          }
        }
      } else {
        await MandatesService.create(newValue).catch((err) => {
          hasError = true;
          formikHelpers.setFieldError(
            value.idx + '',
            err.response?.data?.message ?? 'error'
          );
          displayErrorNotification(err);
        });
      }
    }

    for (let i = 0; i < currentMandates.length; i++) {
      const oguid = currentMandates[i].oguid;
      if (oguid && !Object.values(values).find((val) => val.oguid === oguid)) {
        await MandatesService.delete(oguid).catch((err) => {
          hasError = true;
          setInitPowers({
            [i + '']: err.response?.data?.message ?? 'error',
          });
          displayErrorNotification(err);
        });
      }
    }

    setInProgress?.(false);
    if (!hasError) {
      await dispatch(getMandates()).catch(displayErrorNotification);
      handleClose();
    }
  };

  return powers ? (
    <>
      <h4 className='modal-header'>
        {isEdit
          ? t('profile:PoA.modalTitleEdit') +
            (boxOguid
              ? ' ' + t('profile:PoA.modalTitleEditBox', { box: boxTitle })
              : '')
          : t('profile:PoA.modalTitle')}
      </h4>
      <Formik
        enableReinitialize
        initialValues={powers}
        initialErrors={initErrors}
        innerRef={formRef}
        onSubmit={handleFormSubmit}
        validateOnChange
        validateOnBlur
        validationSchema={isEdit ? powersValidationSchema : undefined}
      >
        {(props) => (
          <CertificatePowerForm
            {...props}
            isEdit={isEdit}
            onClose={handleClose}
            onPoAAdd={handlePoAAdd}
            onPoARemove={handlePoARemove}
            onPoASelect={handlePoASelect}
            isSigning={isSigning}
            isSigningWithout={isSigningWithout}
            boxSelectDisabled={!!boxOguid}
            inProgress={!!inProgress}
          />
        )}
      </Formik>
    </>
  ) : null;
};

export default CertificatePower;
